import React from 'react';
import { ArrowLeft, ChevronRight, Star, ShoppingCart, User, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 11: PRESTIGE - Luxury Fashion, Elegant (Inspired by Shopify's Prestige theme)
const Template11 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-gray-900 min-h-screen text-white font-serif">
            {/* Elegant Header with Full Navigation */}
            <header className="bg-gray-900 border-b border-gray-700 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6">
                    {/* Top Bar */}
                    <div className="flex justify-between items-center py-6">
                        {/* Logo */}
                        <h1
                            onClick={() => navigate('/template11')}
                            className="text-2xl tracking-widest font-light cursor-pointer hover:text-white/80 transition-colors"
                        >
                            PRESTIGE
                        </h1>

                        {/* Right Icons */}
                        <div className="flex items-center gap-6">
                            <Search className="w-5 h-5 text-white/70 cursor-pointer hover:text-white transition-colors" />
                            <Heart className="w-5 h-5 text-white/70 cursor-pointer hover:text-white transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template11/cart')}>
                                <ShoppingCart className="w-5 h-5 text-white/70 hover:text-white transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-white text-gray-900 text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                                    0
                                </span>
                            </div>
                            <User
                                className="w-5 h-5 text-white/70 cursor-pointer hover:text-white transition-colors"
                                onClick={() => navigate('/template11/profile')}
                            />
                        </div>
                    </div>

                    {/* Navigation Menu */}
                    <nav className="border-t border-gray-700">
                        <div className="flex justify-center items-center gap-12 py-4">
                            <button
                                onClick={() => navigate('/template11')}
                                className="text-sm font-light text-white/70 hover:text-white transition-colors uppercase tracking-widest"
                            >
                                Home
                            </button>
                            <button
                                onClick={() => navigate('/template11/shop')}
                                className="text-sm font-light text-white/70 hover:text-white transition-colors uppercase tracking-widest"
                            >
                                Shop
                            </button>
                            <button
                                onClick={() => navigate('/template11/contact')}
                                className="text-sm font-light text-white/70 hover:text-white transition-colors uppercase tracking-widest"
                            >
                                Contact
                            </button>
                            <button
                                onClick={() => navigate('/template11/orders')}
                                className="text-sm font-light text-white/70 hover:text-white transition-colors uppercase tracking-widest"
                            >
                                Orders
                            </button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Full-Screen Hero */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://placehold.co/1920x1080/E0E0E0/333333?text=Designer+Collection"
                        alt="Designer Collection"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                </div>
                <div className="relative h-full flex items-end">
                    <div className="max-w-7xl mx-auto px-6 pb-20 w-full">
                        <div className="max-w-xl">
                            <p className="text-sm tracking-widest mb-4 text-white/70">SPRING COLLECTION 2025</p>
                            <h2 className="text-6xl md:text-7xl font-light mb-6 leading-tight">
                                Timeless<br />Elegance
                            </h2>
                            <p className="text-lg text-white/80 mb-8 font-sans">
                                Discover pieces that define sophistication
                            </p>
                            <button className="group flex items-center gap-3 text-white border-b border-white pb-2 hover:gap-5 transition-all">
                                <span className="text-sm tracking-wider">EXPLORE COLLECTION</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dual Featured Products */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="group cursor-pointer">
                        <div className="relative overflow-hidden aspect-[3/4] mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=1000&fit=crop"
                                alt="Women's Collection"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="text-center">
                            <h3 className="text-3xl font-light mb-2">Women's</h3>
                            <p className="text-white/60 mb-4">Curated luxury</p>
                            <button className="text-sm tracking-wider border-b border-white/30 hover:border-white transition-colors pb-1">
                                SHOP NOW
                            </button>
                        </div>
                    </div>

                    <div className="group cursor-pointer">
                        <div className="relative overflow-hidden aspect-[3/4] mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1488161628813-04466f872be2?w=800&h=1000&fit=crop"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                alt="Men's Collection"
                            />
                        </div>
                        <div className="text-center">
                            <h3 className="text-3xl font-light mb-2">Men's</h3>
                            <p className="text-white/60 mb-4">Refined essentials</p>
                            <button className="text-sm tracking-wider border-b border-white/30 hover:border-white transition-colors pb-1">
                                SHOP NOW
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Grid */}
            <section className="bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Full-Width Statement */}
            <section className="relative h-[500px]">
                <img
                    src="https://images.unsplash.com/photo-1483118714900-540cf339fd46?w=1920&h=800&fit=crop"
                    alt="Statement"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="text-center px-6">
                        <h2 className="text-5xl md:text-6xl font-light mb-6">Crafted for Perfection</h2>
                        <p className="text-xl text-white/80 max-w-2xl mx-auto">
                            Every piece tells a story of meticulous craftsmanship and timeless design
                        </p>
                    </div>
                </div>
            </section>

            {/* Categories Grid */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {['Dresses', 'Accessories', 'Footwear'].map((category, index) => (
                        <div key={index} className="group relative overflow-hidden aspect-[4/5] cursor-pointer">
                            <img
                                src={`https://images.unsplash.com/photo-${1495555961986 + index * 30000}-5e4fcdf4b05f?w=600&h=800&fit=crop`}
                                alt={category}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <h3 className="text-3xl font-light mb-4">{category}</h3>
                                    <span className="text-sm tracking-wider border-b border-white pb-1">
                                        DISCOVER
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">HAUTE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template11')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template11/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template11/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template11/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@haute.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 HAUTE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template11;
